// WebSocket de soporte en "Modo Integración".
//
// Modelo del lado Soporte (AUTORITATIVO):
//   - El ws_token es un JWT corto (TTL ~180s) que SOLO se valida en el HANDSHAKE.
//   - Su expiración NO cierra una conexión ya abierta; el socket queda abierto
//     indefinidamente.
//   - El keepalive es ping/pong del PROTOCOLO WS: el server pinguea ~54s y espera
//     pong en 60s. Como usamos el WebSocket NATIVO del navegador, el pong lo
//     responde el browser automáticamente (NO hay que mandar pings de aplicación).
//   - Por lo tanto: NO se pide token por timer ni se reconecta por temporizador.
//     El token se pide solo al conectar por 1ª vez o tras un onclose/onerror real,
//     y se REUSA mientras siga válido (cacheando su vencimiento).
//
// Endpoints (directos contra soporte, guía §6):
//   - Chat de un ticket:  wss://{BASE}/ws/integration/tickets/{ticketId}?token=<ws_token>
//   - Notificaciones:     wss://{BASE}/ws/integration/notifications?token=<ws_token>

// Resolución del host base del WebSocket de soporte. Acepta tanto un host pelado
// (wss://host) como un valor con path viejo (wss://host/ws/tickets): normaliza a host.
function resolveWSBase() {
  const raw = process.env.VUE_APP_URL_SOPORTES_WS || process.env.VUE_APP_URL_SOPORTES;
  if (raw && String(raw).trim().length > 0) {
    let base = String(raw).trim();
    base = base.replace(/\/(ws|api)(\/.*)?$/i, ""); // quitar path (/ws/..., /api, ...)
    base = base.replace(/^http/i, "ws"); // http→ws, https→wss
    return base.replace(/\/+$/, "");
  }
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}`;
}

function buildPath(kind, ticketId) {
  if (kind === "notifications") return "/ws/integration/notifications";
  return `/ws/integration/tickets/${ticketId}`;
}

/**
 * ChatSocket: conexión WebSocket de soporte (chat de ticket o notificaciones).
 * - getToken: función async que devuelve el ws_token (string) o {ws_token, expires_in}.
 *   Se invoca SOLO al conectar/reconectar, y el token se reusa mientras sea válido.
 * - Reconexión SOLO en onclose/onerror, con backoff exponencial (nunca por timer).
 */
class ChatSocket {
  constructor({ kind = "ticket", ticketId, getToken, onMessage, onStatus }) {
    this.kind = kind;
    this.ticketId = ticketId;
    this.getToken = getToken || (async () => null);
    this.onMessage = onMessage || (() => {});
    this.onStatus = onStatus || (() => {});
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
    this.explicitlyClosed = false;
    // Cache del ws_token para reusarlo mientras siga válido (TTL ~180s).
    this._token = null;
    this._tokenExpiresAt = 0;
  }

  connect() {
    if (this.kind === "ticket" && !this.ticketId) return;
    this.explicitlyClosed = false;
    this._open();
  }

  // Reusa el token cacheado si sigue válido; pide uno nuevo solo si venció o falta.
  async _ensureToken() {
    const now = Date.now();
    if (this._token && now < this._tokenExpiresAt) {
      return this._token;
    }
    const res = await this.getToken();
    const token = typeof res === "string" ? res : res && res.ws_token;
    const ttlSec =
      res && typeof res === "object" && Number(res.expires_in) > 0
        ? Number(res.expires_in)
        : 150;
    this._token = token || null;
    // Margen de 10s para no usarlo justo al borde del vencimiento.
    this._tokenExpiresAt = now + Math.max(0, ttlSec - 10) * 1000;
    return this._token;
  }

  async _open() {
    if (this.kind === "ticket" && !this.ticketId) return;
    this.onStatus("connecting");

    let token = null;
    try {
      token = await this._ensureToken();
    } catch (err) {
      console.error("[ChatSocket] No se pudo obtener el ws_token", err);
      this._token = null;
      this._tokenExpiresAt = 0;
      this.onStatus("error");
      this._scheduleReconnect();
      return;
    }

    if (this.explicitlyClosed) return; // se cerró mientras pedíamos el token
    if (!token) {
      this.onStatus("error");
      this._scheduleReconnect();
      return;
    }

    const base = resolveWSBase();
    const url = `${base}${buildPath(this.kind, this.ticketId)}?token=${encodeURIComponent(token)}`;

    let opened = false;
    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.error("[ChatSocket] Failed to create WebSocket", err);
      this.onStatus("error");
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      opened = true;
      this.reconnectAttempts = 0;
      this.onStatus("open");
      // Keepalive: NO mandamos pings de aplicación. El WebSocket nativo del
      // navegador responde el ping/pong del protocolo automáticamente.
    };

    this.ws.onmessage = (event) => {
      let payload = null;
      try {
        payload = JSON.parse(event.data);
      } catch {
        payload = event.data;
      }
      this.onMessage(payload);
    };

    this.ws.onerror = () => {
      this.onStatus("error");
    };

    this.ws.onclose = (ev) => {
      // Diagnóstico de caídas periódicas: code 1006 (sin close frame) suele ser
      // un corte de proxy/LB (idle timeout); un code/reason del server indica cierre
      // explícito. Sirve para distinguir "infra" de "soporte" sin tocar nada.
      console.warn(
        `[ChatSocket] close kind=${this.kind}` +
          (this.ticketId ? ` ticket=${this.ticketId}` : "") +
          ` code=${ev.code} reason=${ev.reason || "-"} @ ${new Date().toISOString()}`
      );
      this.onStatus("closed");
      // Si se cerró ANTES de abrir, el token pudo ser inválido/expirado (handshake
      // 401): lo invalidamos para pedir uno nuevo en el próximo intento. Si ya
      // estaba abierto, la caída no es por el token → lo reusamos al reconectar.
      if (!opened) {
        this._token = null;
        this._tokenExpiresAt = 0;
      }
      if (!this.explicitlyClosed) this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (this.explicitlyClosed) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts += 1;
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 15000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._open();
    }, delay);
  }

  close() {
    this.explicitlyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this.onStatus("closed");
  }
}

export default ChatSocket;
